<script setup lang="ts">
import { useWordProcessorStore } from "~/components/Base/WordProcessor/useWordProcessorStore";

const documentId = inject("documentId") as any;

const documentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument } = storeToRefs(documentStore);
const { access: writingToolsAccess } =
  useStudentWritingToolsAccess(documentId);

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isCompanionMode } = storeToRefs(documentViewModeStore);

const documentSelfAssessmentStore = useDocumentSelfAssessmentStore(documentId);
const { baseMessageSteps, bannedWordsInstances, bannedWords } = storeToRefs(
  documentSelfAssessmentStore
);

const documentChecklistStore = useDocumentChecklistStore(documentId);
const { numExpectedBodyParagraphs } = storeToRefs(documentChecklistStore);

const wordProcessorStore = useWordProcessorStore(documentId);

const message = ref("");

const markAsComplete = () => {
  documentSelfAssessmentStore.checkForBannedWords(
    wordProcessorStore.getQuillInstance()
  );

  if (message.value == "" && bannedWordsInstances.value.length > 0) {
    message.value =
      "Oops! You used banned words. Look for words with a curvy orange line. Replace banned words with words that show strong image or feeling.";
    return;
  }

  documentSelfAssessmentStore.setActiveStepIndex(0);
};

const isNavigatingBackToChecklist = ref(false);
const backToChecklist = async () => {
  isNavigatingBackToChecklist.value = true;
  useSubmittedDocumentStore(documentId).updateDocumentState(
    DocumentState.editing
  );
  await useSubmittedDocumentStore(documentId).saveDocumentDebounced();
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      class="flex flex-row items-center gap-2 border-b pb-1 pl-4 pr-2 pt-4 justify-between"
    >
      <h2 class="text-h5 font-bold">Initial Review</h2>
      <BaseTextButton
        v-if="
          submittedDocument?.enableStudentSubmissionCreation == true ||
          writingToolsAccess.canWrite == true ||
          isCompanionMode == true
        "
        @click="backToChecklist"
        :show-spinner="isNavigatingBackToChecklist"
      >
        <div class="flex flex-row items-center gap-1 text-body-2">
          <BaseIconListChecks />
          Back to Checklist
        </div>
      </BaseTextButton>
    </div>
    <div class="px-4" v-auto-animate>
      <BaseLabelBadge v-if="message != ''" color="danger" class="mb-2">
        {{ message }}
      </BaseLabelBadge>

      <BaseLabelBadge class="mb-2">
        <div class="flex flex-col gap-2">
          <p>
            Read your
            <span class="text-secondary"
              >{{ numExpectedBodyParagraphs }}-paragraph</span
            >
            composition to make sure sentences are complete and grammatically
            correct.
          </p>
          <p>Check the following:</p>
          <ul class="flex flex-col gap-2 ml-1">
            <li
              v-for="step in baseMessageSteps"
              :key="step.identifier"
              class="flex flex-row items-center gap-1"
            >
              <span>✓</span>
              <span>
                {{ step.hint }}
              </span>
            </li>
            <li class="flex flex-row items-center gap-1">
              <span>✓</span>
              <span>No misspelled words</span>
            </li>
          </ul>
        </div>
      </BaseLabelBadge>

      <div class="flex flex-row items-center justify-end">
        <BaseButton @click="markAsComplete"> I have done this. </BaseButton>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
